// SignersCellPro.tsx
import React, { useMemo } from "react";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import type { ISignatario } from "../types/types";

type Props = {
	signatories?: ISignatario[];
	onOpenDetails?: () => void;
	barWidth?: number;
	barHeight?: number;
	dense?: boolean;
	label?: string;

	/** Quais TIPOS contam como participantes que assinam */
	signingTypes?: string[]; // default: ["signer","witness","guarantor"]

	/** Quais STATUS contam como “assinado / concluído” */
	signedStatuses?: string[]; // default inclui variações comuns

	/** Mostra breakdown por tipo no tooltip (útil para depurar contagem) */
	showTypeBreakdownInTooltip?: boolean;
};

const norm = (s?: string | null) => (s ?? "").trim().toLowerCase();

/** Aliases para lidar com variações do backend */
const TYPE_ALIASES: Record<string, string> = {
	signer: "signer",
	// witness variações
	witness: "witness",
	witnesses: "witness",
	testemunha: "witness",
	// guarantor variações
	guarantor: "guarantor",
	guarantee: "guarantor",
	fiador: "guarantor",
	// observer (não conta)
	observer: "observer",
	observers: "observer",
};

const normalizeType = (t?: string | null) => TYPE_ALIASES[norm(t)] ?? norm(t);

/** Defaults */
const DEFAULT_SIGNING_TYPES = ["signer", "witness", "guarantor"];
const DEFAULT_SIGNED_STATUSES = ["signed", "completed", "finished", "concluded", "finalized", "done", "approved", "accepted"];

export default function SignersCellPro({
	signatories = [],
	onOpenDetails,
	barWidth = 150,
	barHeight = 6,
	dense = false,
	label = "assinaturas",
	signingTypes = DEFAULT_SIGNING_TYPES,
	signedStatuses = DEFAULT_SIGNED_STATUSES,
	showTypeBreakdownInTooltip = false,
}: Props) {
	const theme = useTheme();

	const { total, signed, breakdownText } = useMemo(() => {
		const signTypes = new Set(signingTypes.map(norm));
		const signedSet = new Set(signedStatuses.map(norm));

		// normaliza tipos e mantém só quem assina
		const whoSigns = signatories.map((p) => ({ ...p, _type: normalizeType(p.type), _status: norm(p.status) })).filter((p) => signTypes.has(p._type));

		const total = whoSigns.length;
		const signed = whoSigns.reduce((acc, s) => acc + (signedSet.has(s._status) ? 1 : 0), 0);

		// breakdown por tipo para tooltip
		const byType: Record<string, { total: number; signed: number }> = {};
		for (const p of whoSigns) {
			byType[p._type] ??= { total: 0, signed: 0 };
			byType[p._type].total += 1;
			if (signedSet.has(p._status)) byType[p._type].signed += 1;
		}
		const breakdownParts = Object.entries(byType).map(([t, v]) => `${t}: ${v.signed}/${v.total}`);
		const breakdownText = breakdownParts.join(" • ");

		return { total, signed, breakdownText };
	}, [signatories, signingTypes, signedStatuses]);

	const pct = total > 0 ? signed / total : 0;
	const pctText = `${Math.round(pct * 100)}%`;

	const barColor = useMemo(() => {
		if (pct >= 1) return theme.palette.success.main;
		if (pct >= 0.6) return theme.palette.info.main;
		if (pct > 0) return theme.palette.warning.main;
		return theme.palette.action.disabled;
	}, [pct, theme.palette]);

	const trackBg = alpha(theme.palette.text.primary, 0.1);
	const tooltip = showTypeBreakdownInTooltip
		? `${pctText} — ${signed} de ${total} ${label}${breakdownText ? ` (${breakdownText})` : ""}`
		: `${pctText} — ${signed} de ${total} ${label}`;

	return (
		<Stack
			spacing={dense ? 0.25 : 0.5}
			sx={{ cursor: onOpenDetails ? "pointer" : "default" }}
			onClick={onOpenDetails}
			role={onOpenDetails ? "button" : undefined}
			aria-label={onOpenDetails ? "Abrir detalhes de assinaturas" : undefined}
		>
			<Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
				<Typography variant={dense ? "body2" : "body1"} sx={{ fontWeight: 700, lineHeight: 1 }} title={`${pctText} (${signed} de ${total})`}>
					{pctText}
				</Typography>
				<Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1 }}>
					({signed} de {total}) {label}
				</Typography>
			</Stack>

			<Tooltip title={tooltip}>
				<Box
					sx={{
						width: barWidth,
						height: barHeight,
						borderRadius: 999,
						bgcolor: trackBg,
						overflow: "hidden",
						boxShadow: `inset 0 1px 2px ${alpha(theme.palette.common.black, 0.12)}`,
					}}
				>
					<Box
						sx={{
							width: `${pct * 100}%`,
							height: "100%",
							bgcolor: barColor,
							transition: "width .25s ease",
						}}
					/>
				</Box>
			</Tooltip>
		</Stack>
	);
}
